import type moment from 'moment'
import {
  getCashRefundDeadline,
  getFreeCancellationStartDate,
  showCashRefundCancellation,
  showFlexibleCancellation,
  showFreeCancellation,
} from 'lib/offer/cancellationPolicyUtils'
import {
  getOfferFlexibleCancellationReferenceDate,
  getOfferFlexibleCancellationTimelineContext,
  type OfferFlexibleCancellationTimelineContext,
} from './getOfferFlexibleCancellationTimelineContext'

export interface FlashCancellationPolicyView {
  isCharter: boolean
  policyReferenceDate: string | moment.Moment | undefined
  /**
   * Raw free-cancellation eligibility from `showFreeCancellation` (stable given inputs).
   * Live expiry against wall-clock is applied at render in FlashCancellationPoliciesView so it
   * stays in lockstep with OfferPropertyCancellationPolicies (which also uses fresh `moment()`).
   */
  showFreeCancellation: boolean
  /**
   * Precomputed free-cancel deadline when eligible. Compared to `moment()` at render time —
   * not inside memoized callers — to avoid stale gates vs live child nulls.
   */
  freeCancellationStartDate?: moment.Moment
  /**
   * Mount flexible USP/timeline only when defined.
   * Includes charter-resolving and expired-window hide rules.
   */
  flexibleTimelineContext: OfferFlexibleCancellationTimelineContext | undefined
  /**
   * Non-refundable label gate. Uses raw `showFlexibleCancellation` / free eligibility (not the
   * live free-cancel window) so intentionally-hidden flexible/expired-free states do not flash
   * "Non-refundable".
   */
  showNonRefundable: boolean
  showCashRefund: boolean
  cashRefundDeadline?: moment.Moment
}

interface GetFlashCancellationPolicyViewParams {
  cancellationPolicy?: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  charterDepartDate?: string | moment.Moment
  purchaseDate?: string
  isCharterModeEnabled: boolean
  isChangePackage?: boolean
  availableForPurchase?: boolean
  /** Offer-configured date change allowance: 'Unlimited' or a numeric string like '2'. */
  numberOfDateChangesAllowed?: string
}

/**
 * Single decision result for Flash cancellation surfaces (React + Trip Planner snapshot).
 * Callers should render from this view rather than re-deriving reference dates or eligibility.
 */
export function getFlashCancellationPolicyView(
  params: GetFlashCancellationPolicyViewParams,
): FlashCancellationPolicyView {
  const {
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    isChangePackage,
    availableForPurchase = true,
    numberOfDateChangesAllowed,
  } = params

  const referenceDate = getOfferFlexibleCancellationReferenceDate({
    cancellationPolicy,
    isCharterModeEnabled,
    checkInDate,
    charterDepartDate,
  })
  const policyReferenceDate = referenceDate.timelineReferenceDate

  const freeCancellationEligible = showFreeCancellation(
    policyReferenceDate,
    isChangePackage,
    cancellationPolicy,
  )
  // Snapshot the deadline only — do not compare to wall-clock here. Memoized callers freeze this
  // object; OfferPropertyCancellationPolicies re-checks `startDate?.isBefore(moment())` every render.
  const freeCancellationStartDate = freeCancellationEligible
    ? getFreeCancellationStartDate(policyReferenceDate, purchaseDate, propertyTimezoneOffset)
    : undefined
  // Keep raw eligibility for the non-refundable gate. Mountable flexible UI may still be hidden
  // (charter departure resolving / expired window) without flipping to Non-refundable.
  // Same for expired free-cancel windows: do not flash Non-refundable when free was eligible.
  const flexibleCancellationEligible = showFlexibleCancellation(
    policyReferenceDate,
    isChangePackage,
    cancellationPolicy,
  )
  const cashRefundEligible = showCashRefundCancellation(
    policyReferenceDate,
    isChangePackage,
    cancellationPolicy,
  )

  // Skip context assembly when the raw gate already failed; pass referenceDate to avoid a second pass.
  const flexibleTimelineContext = (
    !isChangePackage &&
    !!cancellationPolicy &&
    flexibleCancellationEligible
  )
    ? getOfferFlexibleCancellationTimelineContext({
        cancellationPolicy,
        propertyTimezoneOffset,
        checkInDate,
        charterDepartDate,
        purchaseDate,
        isCharterModeEnabled,
        availableForPurchase,
        referenceDate,
        numberOfDateChangesAllowed,
      })
    : undefined

  return {
    isCharter: referenceDate.isCharter,
    policyReferenceDate,
    showFreeCancellation: freeCancellationEligible,
    freeCancellationStartDate,
    flexibleTimelineContext,
    showCashRefund: cashRefundEligible,
    cashRefundDeadline: cashRefundEligible ? getCashRefundDeadline(policyReferenceDate, propertyTimezoneOffset) : undefined,
    // Change-package suppresses free/flexible display; leave blank rather than Non-refundable.
    // Missing policy must not render Non-refundable either.
    showNonRefundable: (
      !!cancellationPolicy &&
      !isChangePackage &&
      !freeCancellationEligible &&
      !flexibleCancellationEligible &&
      !cashRefundEligible &&
      cancellationPolicy.type !== 'hidden-cancellation-policy'
    ),
  }
}
